import React, { useState, useEffect } from 'react';
import { Phone, Mail, Clock, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { getImageUrl } from '../lib/imageUtils';

const Header = ({ settings = {}, contactInfo = {} }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const companyName = settings.company_name || 'Narain Trading Co.';
  const tagline = settings.company_tagline || 'Authorized Distributor';
  const logoUrl = getImageUrl(settings.logo_url);
  const topBarPhone = settings.top_bar_phone || contactInfo.phone || '0522 410 2175';
  const topBarEmail = settings.top_bar_email || contactInfo.email || 'info@naraintrading.com';
  const topBarHours = settings.top_bar_hours || 'Mon - Sun: 11 AM - 8 PM';

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <a href={`tel:${topBarPhone.replace(/\s/g, '')}`} className="flex items-center space-x-2 hover:text-yellow-400 transition-colors" data-testid="top-bar-phone">
                <Phone size={14} />
                <span>{topBarPhone}</span>
              </a>
              <a href={`mailto:${topBarEmail}`} className="hidden md:flex items-center space-x-2 hover:text-yellow-400 transition-colors" data-testid="top-bar-email">
                <Mail size={14} />
                <span>{topBarEmail}</span>
              </a>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={14} />
              <span>{topBarHours}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="w-14 h-14 object-contain rounded-lg" />
              ) : (
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">NT</span>
                </div>
              )}
              <div>
                <h1
                  className="text-2xl md:text-3xl font-black tracking-tight leading-none"
                  data-testid="company-name"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 via-blue-800 to-blue-900">Narain</span>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600 italic"> Trading</span>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 via-blue-800 to-blue-900"> Co.</span>
                </h1>
                <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-600 font-semibold mt-0.5">{tagline}</p>
              </div>
            </div>

            <nav className="hidden lg:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-600 font-medium transition-colors" data-testid="nav-home">{settings.nav_home || 'Home'}</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 font-medium transition-colors" data-testid="nav-about">{settings.nav_about || 'About'}</button>
              <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-blue-600 font-medium transition-colors" data-testid="nav-products">{settings.nav_products || 'Products'}</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-600 font-medium transition-colors" data-testid="nav-services">{settings.nav_services || 'Services'}</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 font-medium transition-colors" data-testid="nav-contact">{settings.nav_contact || 'Contact'}</button>
              <Button onClick={() => scrollToSection('contact')} className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-6" data-testid="nav-cta">
                {settings.nav_cta || 'Get Quote'}
              </Button>
            </nav>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-gray-700"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {isMobileMenuOpen && (
            <nav className="lg:hidden py-4 border-t">
              <div className="flex flex-col space-y-4">
                <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-600 font-medium transition-colors text-left">{settings.nav_home || 'Home'}</button>
                <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 font-medium transition-colors text-left">{settings.nav_about || 'About'}</button>
                <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-blue-600 font-medium transition-colors text-left">{settings.nav_products || 'Products'}</button>
                <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-600 font-medium transition-colors text-left">{settings.nav_services || 'Services'}</button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 font-medium transition-colors text-left">{settings.nav_contact || 'Contact'}</button>
                <Button onClick={() => scrollToSection('contact')} className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white w-full">
                  {settings.nav_cta || 'Get Quote'}
                </Button>
              </div>
            </nav>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
